<template>
	<div class="pa-2">
		<B-confirm-edit-dialog
			ref="dialog"
			@on-consent="addEmployee"
		></B-confirm-edit-dialog>
		<v-row dense>
			<!-- academic -->
			<v-col cols="12">
				<v-card
					height="100%"
					class="pa-2 block-border mb-2"
					elevation="0">
					<strong class="d-block mb-2">{{ $T('academic.title') }}</strong>
					<bee-handy-smart-table
						ref="academicTable"
						v-model="academic.inputs"
						:headers="academic.headers"
						:items="academic.items"
						@keypress.enter="academic.editId === null ? academic.add() : academic.update()"
						:items-per-page="-1"
						:data-table-props="{ disableSort: true }"
						:readonly-input-fields="inputsAttr.tableDisable"
						dense
						elevation="0"
						editable-rows
						hide-no-data-area
						show-add-row
						hide-default-footer
						fixed-header
						hide-inputs-details>
						<template v-slot:input.degree="{ on, attr }">
							<v-select
								:items="Object.values(degrees)"
								v-on="on"
								v-bind="attr"></v-select>
						</template>
						<template v-slot:input.specification="{ on, attr }">
							<b-async-input :storeName="'filters/fetchSpecificationFilters'">
								<template v-slot:default="{ items, loading, focus }">
									<v-combobox
										:items="items"
										:loading="loading"
										item-text="specialization"
										v-on="on"
										v-bind="attr"
										@focus="focus"></v-combobox>
								</template>
							</b-async-input>
						</template>
						<template v-slot:item.specification="{ item }">
							{{ item.specification?.specialization || item.specification }}
						</template>
						<template v-slot:item.graduated="{ item }">
							<div style="width: max-content">
								{{
									item.graduated
										? $T('academic.isGraduated')
										: $T('academic.isNotGraduated')
								}}
							</div>
						</template>
						<template v-slot:input.graduated="{ on, attr }">
							<!-- @keypress.native="on.keypress" -->
							<v-checkbox
								@change="(c) => on.input(c)"
								v-on="on"
								v-bind="attr"
								:input-value="attr?.value"></v-checkbox>
						</template>
						<template v-slot:item.actions="{ item, index }">
							<div class="d-flex">
								<tooltip
									:text="$T('tooltips.edit')"
									:disabled="inputsAttr.tableDisable">
									<v-btn
										:disabled="inputsAttr.tableDisable"
										depressed
										rounded
										icon
										small
										@click="
											academic.ref.activeEditForRow(index);
											academic.editId = item.id;
										">
										<v-icon
											size="20"
											color="edit-color">
											$edit
										</v-icon>
									</v-btn>
								</tooltip>
								<tooltip
									:text="$T('tooltips.delete')"
									:disabled="inputsAttr.tableDisable">
									<v-btn
										:disabled="inputsAttr.tableDisable"
										icon
										small
										@click="academic.delete(index)">
										<v-icon
											size="20"
											color="alert">
											$delete
										</v-icon>
									</v-btn>
								</tooltip>
							</div>
						</template>
						<template v-slot:no-input.actions="{}">
							<tooltip
								:text="$T('tooltips.addRow')"
								:disabled="inputsAttr.tableDisable">
								<v-btn
									:disabled="inputsAttr.tableDisable"
									depressed
									rounded
									icon
									small
									color="success"
									@click="academic.editId === null ? academic.add() : academic.update()">
									<v-icon
										size="20"
										color="addRow">
										{{ 'mdi-check-circle-outline' }}
									</v-icon>
								</v-btn>
							</tooltip>
						</template>
					</bee-handy-smart-table>
				</v-card>
			</v-col>

			<!-- language -->
			<v-col
				cols="12"
				md="4">
				<v-card
					height="220px"
					class="pa-2 block-border mb-2"
					elevation="0">
					<strong class="d-block mb-2">{{ $T('language.title') }}</strong>
					<bee-handy-smart-table
						height="170px"
						ref="languageTable"
						v-model="language.inputs"
						:headers="language.headers"
						:items="language.items"
						@keypress.enter="language.editId === null ? language.add() : language.update()"
						:items-per-page="-1"
						:data-table-props="{ disableSort: true }"
						:readonly-input-fields="inputsAttr.tableDisable"
						dense
						elevation="0"
						editable-rows
						hide-no-data-area
						show-add-row
						hide-default-footer
						fixed-header
						hide-inputs-details
						class="scroll"
						>
						<template v-slot:input.language="{ on, attr }">
							<b-async-input :storeName="'filters/fetchLanguageFilters'">
								<template v-slot:default="{ items, loading, focus }">
									<v-combobox
										:items="items"
										:loading="loading"
										item-text="name"
										v-on="on"
										v-bind="attr"
										@focus="focus"
										></v-combobox>
								</template>
							</b-async-input>
						</template>
						<template v-slot:item.language="{ item }">
							{{ item.language.name || item.language }}
						</template>
						<template v-slot:input.skill="{ on, attr }">
							<div class="level-input">
								<v-rating
									dense
									length="5"
									:value="language.inputs?.skill">
									<template v-slot:item="{ isFilled }">
										<v-icon
											size="20"
											color="primary">
											{{ isFilled ? '$ratingFull' : '$ratingEmpty' }}
										</v-icon>
									</template>
								</v-rating>
								<v-slider
									@keypress.native="on.keypress"
									v-on="on"
									v-bind="attr"
									min="1"
									max="5"
									step="1"
									tick-size="20"
									ticks="always"></v-slider>
							</div>
						</template>
						<template v-slot:item.skill="{ item }">
							<v-rating
								hover
								dense
								length="5"
								size="20"
								readonly
								:value="item.skill"></v-rating>
						</template>
						<template v-slot:item.actions="{ item, index }">
							<div class="d-flex">
								<tooltip
									:text="$T('tooltips.edit')"
									:disabled="inputsAttr.tableDisable">
									<v-btn
										:disabled="inputsAttr.tableDisable"
										depressed
										rounded
										icon
										small
										@click="
											language.ref.activeEditForRow(index);
											language.editId = item.id;
										">
										<v-icon
											size="20"
											color="edit-color">
											$edit
										</v-icon>
									</v-btn>
								</tooltip>
								<tooltip
									:text="$T('tooltips.delete')"
									:disabled="inputsAttr.tableDisable">
									<v-btn
										:disabled="inputsAttr.tableDisable"
										icon
										small
										@click="language.delete(index)">
										<v-icon
											size="20"
											color="alert">
											$delete
										</v-icon>
									</v-btn>
								</tooltip>
							</div>
						</template>
						<template v-slot:no-input.actions="{}">
							<tooltip
								:text="$T('tooltips.addRow')"
								:disabled="inputsAttr.tableDisable">
								<v-btn
									:disabled="inputsAttr.tableDisable"
									depressed
									rounded
									icon
									small
									color="success"
									@click="language.editId === null ? language.add() : language.update()">
									<v-icon
										size="20"
										color="addRow">
										{{ 'mdi-check-circle-outline' }}
									</v-icon>
								</v-btn>
							</tooltip>
						</template>
					</bee-handy-smart-table>
				</v-card>
			</v-col>

			<!-- computer skills -->
			<v-col
				cols="12"
				md="4">
				<v-card
					height="220px"
					class="pa-2 block-border mb-2"
					elevation="0">
					<strong class="d-block mb-2">{{ $T('computer.title') }}</strong>
					<bee-handy-smart-table
						height="170px"
						ref="computerTable"
						v-model="computer.inputs"
						:headers="computer.headers"
						:items="computer.items"
						@keypress.enter="computer.editId === null ? computer.add() : computer.update()"
						:items-per-page="-1"
						:data-table-props="{ disableSort: true }"
						:readonly-input-fields="inputsAttr.tableDisable"
						dense
						elevation="0"
						editable-rows
						hide-no-data-area
						show-add-row
						hide-default-footer
						fixed-header
						hide-inputs-details
						class="scroll"
						>
						<template v-slot:input.name="{ on, attr }">
							<b-async-input :storeName="'filters/fetchComputerFilters'">
								<template v-slot:default="{ items, loading, focus }">
									<v-combobox
										:items="items"
										:loading="loading"
										item-text="name"
										v-on="on"
										v-bind="attr"
										@focus="focus"
										></v-combobox>
								</template>
							</b-async-input>
						</template>
						<template v-slot:item.name="{ item }">
							{{ item.name.name || item.name }}
						</template>
						<template v-slot:input.skill="{ on, attr }">
							<div class="level-input">
								<v-rating
									dense
									length="5"
									:value="computer.inputs?.skill">
									<template v-slot:item="{ isFilled }">
										<v-icon
											size="20"
											color="primary">
											{{ isFilled ? '$ratingFull' : '$ratingEmpty' }}
										</v-icon>
									</template>
								</v-rating>
								<v-slider
									@keypress.native="on.keypress"
									v-on="on"
									v-bind="attr"
									min="1"
									max="5"
									step="1"
									tick-size="20"
									ticks="always"></v-slider>
							</div>
						</template>
						<template v-slot:item.skill="{ item }">
							<v-rating
								hover
								dense
								length="5"
								size="20"
								readonly
								:value="item.skill"></v-rating>
						</template>
						<template v-slot:item.actions="{ item, index }">
							<div class="d-flex">
								<tooltip
									:text="$T('tooltips.edit')"
									:disabled="inputsAttr.tableDisable">
									<v-btn
										:disabled="inputsAttr.tableDisable"
										depressed
										rounded
										icon
										small
										@click="
											computer.ref.activeEditForRow(index);
											computer.editId = item.id;
										">
										<v-icon
											size="20"
											color="edit-color">
											$edit
										</v-icon>
									</v-btn>
								</tooltip>
								<tooltip
									:text="$T('tooltips.delete')"
									:disabled="inputsAttr.tableDisable">
									<v-btn
										:disabled="inputsAttr.tableDisable"
										icon
										small
										@click="computer.delete(index)">
										<v-icon
											size="20"
											color="alert">
											$delete
										</v-icon>
									</v-btn>
								</tooltip>
							</div>
						</template>
						<template v-slot:no-input.actions="{}">
							<tooltip
								:text="$T('tooltips.addRow')"
								:disabled="inputsAttr.tableDisable">
								<v-btn
									:disabled="inputsAttr.tableDisable"
									depressed
									rounded
									icon
									small
									color="success"
									@click="computer.editId === null ? computer.add() : computer.update()">
									<v-icon
										size="20"
										color="addRow">
										{{ 'mdi-check-circle-outline' }}
									</v-icon>
								</v-btn>
							</tooltip>
						</template>
					</bee-handy-smart-table>
				</v-card>
			</v-col>

			<!-- expertise skills -->
			<v-col
				cols="12"
				md="4">
				<v-card
					height="220px"
					class="pa-2 block-border mb-2"
					elevation="0">
					<strong class="d-block mb-2">{{ $T('expertise.title') }}</strong>
					<bee-handy-smart-table
						height="170px"
						ref="expertiseTable"
						v-model="expertise.inputs"
						:headers="expertise.headers"
						:items="expertise.items"
						@keypress.enter="expertise.editId === null ? expertise.add() : expertise.update()"
						:items-per-page="-1"
						:data-table-props="{ disableSort: true }"
						:readonly-input-fields="inputsAttr.tableDisable"
						dense
						elevation="0"
						editable-rows
						hide-no-data-area
						show-add-row
						hide-default-footer
						fixed-header
						hide-inputs-details
						class="scroll"
						>
						<template v-slot:input.name="{ on, attr }">
							<b-async-input :storeName="'filters/fetchExpertiseFilters'">
								<template v-slot:default="{ items, loading, focus }">
									<v-combobox
										:items="items"
										:loading="loading"
										item-text="name"
										v-on="on"
										v-bind="attr"
										@focus="focus"></v-combobox>
								</template>
							</b-async-input>
						</template>
						<template v-slot:item.name="{ item }">
							{{ item.name.name || item.name }}
						</template>
						<template v-slot:input.skill="{ on, attr }">
							<div class="level-input">
								<v-rating
									dense
									length="5"
									:value="expertise.inputs?.skill">
									<template v-slot:item="{ isFilled }">
										<v-icon
											size="20"
											color="primary">
											{{ isFilled ? '$ratingFull' : '$ratingEmpty' }}
										</v-icon>
									</template>
								</v-rating>
								<v-slider
									@keypress.native="on.keypress"
									v-on="on"
									v-bind="attr"
									min="1"
									max="5"
									step="1"
									tick-size="20"
									ticks="always"></v-slider>
							</div>
						</template>
						<template v-slot:item.skill="{ item }">
							<v-rating
								hover
								dense
								length="5"
								size="20"
								readonly
								:value="item.skill"></v-rating>
						</template>
						<template v-slot:item.actions="{ item, index }">
							<div class="d-flex">
								<tooltip
									:text="$T('tooltips.edit')"
									:disabled="inputsAttr.tableDisable">
									<v-btn
										:disabled="inputsAttr.tableDisable"
										depressed
										rounded
										icon
										small
										@click="
											expertise.ref.activeEditForRow(index);
											expertise.editId = item.id;
										">
										<v-icon
											size="20"
											color="edit-color">
											$edit
										</v-icon>
									</v-btn>
								</tooltip>
								<tooltip
									:text="$T('tooltips.delete')"
									:disabled="inputsAttr.tableDisable">
									<v-btn
										:disabled="inputsAttr.tableDisable"
										icon
										small
										@click="expertise.delete(index)">
										<v-icon
											size="20"
											color="alert">
											$delete
										</v-icon>
									</v-btn>
								</tooltip>
							</div>
						</template>
						<template v-slot:no-input.actions="{}">
							<tooltip
								:text="$T('tooltips.addRow')"
								:disabled="inputsAttr.tableDisable">
								<v-btn
									:disabled="inputsAttr.tableDisable"
									depressed
									rounded
									icon
									small
									color="success"
									@click="expertise.editId === null ? expertise.add() : expertise.update()">
									<v-icon
										size="20"
										color="addRow">
										{{ 'mdi-check-circle-outline' }}
									</v-icon>
								</v-btn>
							</tooltip>
						</template>
					</bee-handy-smart-table>
				</v-card>
			</v-col>

			<!-- courses skills -->
			<v-col
				cols="12"
				class="mb-2">
				<v-card
					height="100%"
					class="pa-2 block-border mb-2"
					elevation="0">
					<strong class="d-block mb-2">{{ $T('courses.title') }}</strong>
					<bee-handy-smart-table
						ref="coursesTable"
						v-model="courses.inputs"
						:headers="courses.headers"
						:items="courses.items"
						@keypress.enter="courses.editId === null ? courses.add() : courses.update()"
						:items-per-page="-1"
						:data-table-props="{ disableSort: true }"
						:readonly-input-fields="inputsAttr.tableDisable"
						elevation="0"
						dense=""
						editable-rows
						hide-no-data-area
						show-add-row
						hide-default-footer
						fixed-header
						hide-inputs-details
						stay-in-input-fields>
						<template v-slot:input.name="{ on, attr }">
							<b-async-input :storeName="'filters/fetchCoursesFilters'">
								<template v-slot:default="{ items, loading, focus }">
									<v-combobox
										:items="items"
										:loading="loading"
										item-text="name"
										v-on="on"
										v-bind="attr"
										@focus="focus"></v-combobox>
								</template>
							</b-async-input>
						</template>
						<template v-slot:item.name="{ item }">
							{{ item.name.name || item.name }}
						</template>
						<template v-slot:item.certificate="{ item }">
							<div style="width: max-content">
								{{
									item.certificate ? $T('courses.certificate') : $T('courses.certificateNo')
								}}
							</div>
						</template>
						<template v-slot:input.certificate="{ on, attr }">
							<v-checkbox
								@change="(c) => on.input(c)"
								@keypress.native="on.keypress"
								v-on="on"
								v-bind="attr"
								:input-value="attr?.value"></v-checkbox>
						</template>
						<template v-slot:item.actions="{ item, index }">
							<div class="d-flex">
								<tooltip
									:text="$T('tooltips.edit')"
									:disabled="inputsAttr.tableDisable">
									<v-btn
										:disabled="inputsAttr.tableDisable"
										depressed
										rounded
										icon
										small
										@click="
											courses.ref.activeEditForRow(index);
											courses.editId = item.id;
										">
										<v-icon
											size="20"
											color="edit-color">
											$edit
										</v-icon>
									</v-btn>
								</tooltip>
								<tooltip
									:text="$T('tooltips.delete')"
									:disabled="inputsAttr.tableDisable">
									<v-btn
										:disabled="inputsAttr.tableDisable"
										icon
										small
										@click="courses.delete(index)">
										<v-icon
											size="20"
											color="alert">
											$delete
										</v-icon>
									</v-btn>
								</tooltip>
							</div>
						</template>
						<template v-slot:no-input.actions="{}">
							<tooltip
								:text="$T('tooltips.addRow')"
								:disabled="inputsAttr.tableDisable">
								<v-btn
									:disabled="inputsAttr.tableDisable"
									depressed
									rounded
									icon
									small
									color="success"
									@click="courses.editId === null ? courses.add() : courses.update()">
									<v-icon
										size="20"
										color="addRow">
										{{ 'mdi-check-circle-outline' }}
									</v-icon>
								</v-btn>
							</tooltip>
						</template>
					</bee-handy-smart-table>
				</v-card>
			</v-col>

			<v-col cols="12">
				<v-textarea
					v-model="notes"
					:label="$T('inputs.notes')"
					rows="3"
					outlined
					dense
					hide-details
					auto-grow
					v-bind="inputsAttr"></v-textarea>
			</v-col>
		</v-row>
		<v-divider class="mt-4 mb-3"></v-divider>
		<div class="d-flex justify-end">
			<v-btn
				color="primary"
				:to="{ name: 'personal-info', query: { employeeId: $route.query.employeeId } }">
				<v-icon left> mdi-share </v-icon>
				{{ $T('default-buttons.prev') }}
			</v-btn>
			<v-spacer></v-spacer>
			<v-btn
				class="px-10 mx-2"
				@click="$refs.dialog.activeConfirmDialog()"
				color="success"
				:loading="loadingAddingEmployee || loadingFetchEmployee"
				:disabled="loadingAddingEmployee || loadingFetchEmployee || disableSave">
				{{ employeeId ? 'تعديل ملف الموظف' : 'إنشاء ملف الموظف' }}
			</v-btn>
			<v-btn
				color="primary"
				:to="{ name: 'practical-experiences', query: { employeeId: $route.query.employeeId } }">
				{{ $T('default-buttons.next') }}
				<v-icon right> mdi-reply </v-icon>
			</v-btn>
		</div>
	</div>
</template>

<script>
import { DATE_FORMAT } from '@/utils/moment';
import { degrees } from '@/utils/enum';
import { TableLocale, showErrorMessagesFun } from '@/utils/helpers';
import BConfirmEditDialog from '@/components/forms/BConfirmEditDialog'

export default {
	name: 'AcademicQualifications',
	components: { BConfirmEditDialog },
	props: {
		loadingFetchEmployee: { type: Boolean },
		loadingAddingEmployee: { type: Boolean },
		employeeInfo: { type: Object, default: () => ({}) },
		employeeId: { type: String },
		editingEmployee: { type: Boolean, default: false },
		disableSave: { type: Boolean, default: false },
	},

	data() {
		const academicHeaders = [
			{
				text: this.$T('academic.degree'),
				name: 'degree',
				value: 'degree',
				noNotAvailableText: true,
				setValueExpr: (value) => Object.values(degrees).find((e) => e.value === value)?.text,
				sortable: false
			},
			{
				text: this.$T('academic.school'),
				name: 'school',
				value: 'school',
				sortable: false,
			},
			{
				text: this.$T('academic.specification'),
				name: 'specification',
				value: 'specification',
				sortable: false,
			},
			{
				text: this.$T('academic.graduated'),
				name: 'graduated',
				value: 'graduated',
				sortable: false,
				width: 20,
			},
			{
				text: this.$T('academic.year'),
				name: 'year',
				value: 'year',
				sortable: false,
				type: 'number'
			},
			{
				text: this.$T('academic.gpa'),
				name: 'gpa',
				value: 'gpa',
				sortable: false,
			},
			{
				text: '',
				name: 'actions',
				value: 'actions',
				noInput: true,
				width: 20,
			},
		];
		const academicValidation = [
			{ for: ['degree'], fun: (e) => e !== null, message: this.$T('validations.degreeRequire') },
			{
				for: ['gpa'],
				fun: (e) => e === null || e === '' || Number(e),
				message: this.$T('validations.averageInt'),
			},
			{ require: 'school', message: this.$T('validations.schoolRequire') },
			{ require: 'specification', message: this.$T('validations.specificationRequire') },
		];
		const academic = new TableLocale(academicHeaders, {
			validation: academicValidation,
			showErrorMessages: true,
			showErrorMessagesFun: showErrorMessagesFun(this),
		});

		const languageHeaders = [
			{
				text: this.$T('language.name'),
				name: 'language',
				value: 'language',
				sortable: false,
			},
			{
				text: this.$T('language.skill'),
				name: 'skill',
				value: 'skill',
				sortable: false,
			},
			{
				text: '',
				name: 'actions',
				value: 'actions',
				noInput: true,
				width: 20,
			},
		];
		const languageValidation = [{ require: 'language', message: 'اسم اللغة مطلوب' }];
		const language = new TableLocale(languageHeaders, {
			validation: languageValidation,
			showErrorMessages: true,
			showErrorMessagesFun: showErrorMessagesFun(this),
		});

		const computerHeaders = [
			{
				text: this.$T('computer.name'),
				name: 'name',
				value: 'name',
				sortable: false,
			},
			{
				text: this.$T('computer.skill'),
				name: 'skill',
				value: 'skill',
				sortable: false,
			},
			{
				text: '',
				name: 'actions',
				value: 'actions',
				noInput: true,
			},
		];
		const computerValidation = [{ require: 'name', message: 'اسم مهارات الحاسوب مطلوب' }];
		const computer = new TableLocale(computerHeaders, {
			validation: computerValidation,
			showErrorMessages: true,
			showErrorMessagesFun: showErrorMessagesFun(this),
		});

		const expertiseHeaders = [
			{
				text: this.$T('expertise.name'),
				name: 'name',
				value: 'name',
				sortable: false,
			},
			{
				text: this.$T('expertise.skill'),
				name: 'skill',
				value: 'skill',
				sortable: false,
			},
			{
				text: '',
				name: 'actions',
				value: 'actions',
				noInput: true,
				width: 20,
			},
		];
		const expertiseValidation = [{ require: 'name', message: 'اسم المهارة مطلوب' }];
		const expertise = new TableLocale(expertiseHeaders, {
			validation: expertiseValidation,
			showErrorMessages: true,
			showErrorMessagesFun: showErrorMessagesFun(this),
		});

		const coursesHeaders = [
			{ text: this.$T('courses.name'), name: 'name', value: 'name', sortable: false },
			{
				text: this.$T('courses.date'),
				name: 'date',
				value: 'date',
				type: 'date',
				dateFormat: DATE_FORMAT,
				width: '20%',
				sortable: false,
			},
			{
				text: this.$T('courses.duration'),
				name: 'duration',
				value: 'duration',
				sortable: false,
			},
			{
				text: this.$T('courses.place'),
				name: 'place',
				value: 'place',
				sortable: false,
			},
			{
				text: this.$T('courses.organization'),
				name: 'organization',
				value: 'organization',
				sortable: false,
			},
			{
				text: this.$T('courses.certificate'),
				name: 'certificate',
				value: 'certificate',
				sortable: false,
			},
			{
				text: '',
				name: 'actions',
				value: 'actions',
				noInput: true,
			},
		];
		const coursesValidation = [
			{ require: 'name', message: this.$T('validations.nameRequire') },
			{ require: 'date', message: this.$T('validations.dateRequire') },
			{ require: 'organization', message: this.$T('validations.organizationRequire') },
			{ for: ['duration'], fun: (e) => Number(e), message: this.$T('validations.durationInt') },
		];
		const courses = new TableLocale(coursesHeaders, {
			validation: coursesValidation,
			showErrorMessages: true,
			showErrorMessagesFun: showErrorMessagesFun(this),
		});

		return {
			academic,
			language,
			expertise,
			computer,
			courses,
			notes: null,

			degrees,
		};
	},

	watch: {
		employeeInfo() {
			this.setInfo();
		},
	},

	computed: {
		inputsAttr() {
			return {
				readonly: this.editingEmployee,
				disabled: this.editingEmployee,
				tableDisable: this.editingEmployee,
			};
		},
	},

	methods: {
		setInfo() {
			if (this.employeeInfo.academic) {
				this.academic.items = [];
				this.employeeInfo.academic.forEach((e, i) => {
					this.academic.items.push({ ...e, id: ++this.academic.counter });
				});
			}
			if (this.employeeInfo.language) {
				this.language.items = [];
				this.employeeInfo.language.forEach((e, i) => {
					this.language.items.push({ ...e, id: ++this.language.counter });
				});
			}
			if (this.employeeInfo.computer) {
				this.computer.items = [];
				this.employeeInfo.computer.forEach((e, i) => {
					this.computer.items.push({ ...e, id: ++this.computer.counter });
				});
			}
			if (this.employeeInfo.expertise) {
				this.expertise.items = [];
				this.employeeInfo.expertise.forEach((e, i) => {
					this.expertise.items.push({ ...e, id: ++this.expertise.counter });
				});
			}
			if (this.employeeInfo.courses) {
				this.courses.items = [];
				this.employeeInfo.courses.forEach((e, i) => {
					this.courses.items.push({ ...e, id: ++this.courses.counter });
				});
			}

			this.notes = this.employeeInfo.academicNote;
		},
		getInfos() {
			return {
				data: {
					academic: this.academic.items,
					language: this.language.items,
					computer: this.computer.items,
					expertise: this.expertise.items,
					courses: this.courses.items,
					academicNote: this.notes,
				},
			};
		},
		addEmployee() {
			this.$refs.dialog.deactivateConfirmDialog()
			this.$emit('add-employee', this.getInfos().data);
		},
	},

	mounted() {
		this.$nextTick(() => {
			this.language.ref = this.$refs.languageTable;
			this.academic.ref = this.$refs.academicTable;
			this.expertise.ref = this.$refs.expertiseTable;
			this.computer.ref = this.$refs.computerTable;
			this.courses.ref = this.$refs.coursesTable;
		});

		this.setInfo();
	},
};
</script>

<style scoped lang="scss">

</style>
